import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';

import FormProvider, { RHFTextField } from '../../../components/hook-form';
import { TableHeadCustom, TableNoData } from '../../../components/table';
import OrderSummary from './OrderSummary';
import moment from 'moment';
import { TillService } from '../../../api/till.service';
import { capitalize } from 'lodash';
import { useAuthContext } from '../../../auth/useAuthContext';
import { enqueueSnackbar } from '../../../components/snackbar';
import useBarcodeScanner from '../../../hooks/useBarcodeScanner';
import { ProductService } from '../../../api/product.service';
import { PATH_DASHBOARD } from '../../../routes/paths';
import {
  TILL_BARCODE_NAV_STATE_KEY,
  activeProductToDispenseLine,
  findProductByBarcode,
} from './tillBarcodeShared';
import { getDispenseTillDisplayInstant } from './tillDispenseDisplay';
import { readTillFocusDispenseId } from './tillNavShared';

// Updated interface for API response
interface TillApiResponse {
  till: {
    id: number;
    wallet: string;
    dispenseTills: [
      {
        amount: number;
        status: string;
        dispenseId: number;
        paymentDate: any;
        paymentMethod: any;
        refundedAt?: string | null;
        refundReason?: string;
        refundMode?: string;
        refundAmount?: number;
      },
    ];
  };
  patient: {
    id: number;
    firstName: string;
    surName: string;
    lastName: string;
    phone: string;
    email: string;
  };
}

const TABLE_HEAD = [
  { id: 'dispenseId', label: 'Dispense ID', align: 'center' },
  { id: 'amount', label: 'Amount', align: 'center' },
  { id: 'status', label: 'Status', align: 'center' },
  { id: 'paymentDate', label: 'Payment / Refund date', align: 'center' },
  { id: 'paymentMethod', label: 'Payment Method', align: 'center' },
  { id: 'action', label: 'Action', align: 'center' },
];

function loggedInStaffDisplayName(user: Record<string, unknown> | null) {
  if (!user) return '';
  const u = user as Record<string, string | undefined>;
  const username = String(u.username ?? '').trim();
  if (username) return username;
  const fromParts = [u.firstName, u.lastName, u.surName]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (fromParts) return fromParts;
  const name = String(u.name ?? '').trim();
  if (name) return name;
  return String(u.email ?? '').trim();
}

export default function TillPage() {
  const { user } = useAuthContext();
  const staffDisplayName = loggedInStaffDisplayName(user);

  const [cashExpanded, setCashExpanded] = useState(false);
  const [cardExpanded, setCardExpanded] = useState(false);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [voucherAmount, setVoucherAmount] = useState<string>('');

  const [open, setOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [tillApiData, setTillApiData] = useState<TillApiResponse | null>(null);
  const [selectedDispenseTill, setSelectedDispenseTill] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { patientData } = location.state || {};
  // Set when arriving from a dispense/sale save: the row to pre-select.
  const focusDispenseId = readTillFocusDispenseId(location.state);
  const [scanProducts, setScanProducts] = useState<any[]>([]);

  // Always view mode
  const isViewMode = true;

  const methods = useForm({
    defaultValues: {
      name: patientData?.name || '',
      phone: patientData?.phone || '',
      email: patientData?.email || '',
      date: '',
      time: '',
      wallet: '',
    },
  });

  const { reset } = methods;

  const { tillId } = useParams();

  // Updated getTill function to handle the API response
  const getTill = async (options?: { keepDispenseId?: number }) => {
    try {
      setLoading(true);
      if (tillId) {
        const tillService = new TillService();
        const response = await tillService.getTillData(Number(tillId));
        setTillApiData(response.data);

        const list = response.data?.till?.dispenseTills ?? [];
        if (list.length > 0) {
          const keepId = options?.keepDispenseId;
          const match =
            keepId != null
              ? list.find((d: any) => d.dispenseId === keepId)
              : null;
          setSelectedDispenseTill(match || list[0]);
        } else {
          setSelectedDispenseTill(null);
        }
      }
    } catch (error) {
      console.error('Error fetching till data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle selecting a dispenseTill
  const handleSelectDispenseTill = (dispenseTill: any) => {
    setSelectedDispenseTill(dispenseTill);
  };

  // Calculate outstanding balance
  const getOutstandingBalance = () => {
    if (!selectedDispenseTill) return 0;
    const payments = selectedDispenseTill.payments || [];
    const totalPaid = payments.reduce(
      (sum: number, p: any) => sum + Number(p?.amount ?? 0),
      0,
    );
    const amountPaid =
      totalPaid > 0
        ? totalPaid
        : selectedDispenseTill.status === 'paid'
          ? selectedDispenseTill.amount
          : 0;
    return Math.max(0, Number(selectedDispenseTill.amount) - amountPaid);
  };

  const walletBalanceNum = () =>
    parseFloat(String(tillApiData?.till?.wallet ?? 0)) || 0;

  const walletPayAmount = () =>
    Math.min(walletBalanceNum(), getOutstandingBalance());

  const handleRecordPayment = async (method: string, amount: string) => {
    if (
      !amount ||
      isNaN(Number(amount)) ||
      !tillApiData ||
      !selectedDispenseTill
    )
      return;
    try {
      setLoading(true);
      const tillService = new TillService();
      await tillService.recordPayment(
        tillApiData.patient.id,
        selectedDispenseTill.dispenseId,
        {
          amount: Number(amount),
          paymentMethod: method,
        },
      );
      // Refresh data
      await getTill({
        keepDispenseId: selectedDispenseTill.dispenseId,
      });
      // Reset states
      setCashExpanded(false);
      setCardExpanded(false);
      setVoucherOpen(false);
      setPaymentAmount('');
      setVoucherAmount('');
    } catch (error) {
      console.error('Error recording payment:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Stripe payment for selected dispenseTill
  const handleStripePayment = async () => {
    if (!tillApiData?.till?.id || !selectedDispenseTill) {
      console.error('Till ID or selected dispense till not available');
      return;
    }

    try {
      setStripeLoading(true);
      const tillService = new TillService();
      const response = await tillService.payWithStripe(
        tillApiData.patient.id,
        selectedDispenseTill.dispenseId,
      );

      // The Stripe API will handle the redirect to Stripe checkout
      if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error processing Stripe payment:', error);
      // Handle error (show toast notification, etc.)
    } finally {
      setStripeLoading(false);
    }
  };

  const handleRefund = async (mode: 'refund' | 'wallet') => {
    const reason = refundReason.trim();
    if (!reason) {
      enqueueSnackbar('Please enter a refund reason.', { variant: 'warning' });
      return;
    }
    if (!tillApiData || !selectedDispenseTill) return;
    try {
      setRefundSubmitting(true);
      const tillService = new TillService();
      await tillService.refundDispenseTill(
        tillApiData.patient.id,
        selectedDispenseTill.dispenseId,
        { reason, mode },
      );
      enqueueSnackbar(
        mode === 'wallet'
          ? 'Refund credited to patient wallet.'
          : 'Refund recorded successfully.',
        { variant: 'success' },
      );
      setRefundOpen(false);
      setRefundReason('');
      await getTill({
        keepDispenseId: selectedDispenseTill.dispenseId,
      });
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      const text = Array.isArray(msg)
        ? msg.join(', ')
        : typeof msg === 'string'
          ? msg
          : error?.message || 'Refund failed';
      enqueueSnackbar(text, { variant: 'error' });
    } finally {
      setRefundSubmitting(false);
    }
  };

  const handlePayFromWallet = async () => {
    if (!tillApiData || !selectedDispenseTill) return;
    const outstanding = getOutstandingBalance();
    if (outstanding <= 0 || walletBalanceNum() <= 0) {
      enqueueSnackbar('Nothing to pay from wallet.', { variant: 'warning' });
      return;
    }
    try {
      setLoading(true);
      const tillService = new TillService();
      await tillService.payFromWallet(
        tillApiData.patient.id,
        selectedDispenseTill.dispenseId,
      );
      enqueueSnackbar('Wallet payment applied.', { variant: 'success' });
      setOpen(false);
      await getTill({ keepDispenseId: selectedDispenseTill.dispenseId });
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      const text = Array.isArray(msg)
        ? msg.join(', ')
        : typeof msg === 'string'
          ? msg
          : error?.message || 'Wallet payment failed';
      enqueueSnackbar(text, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tillApiData && selectedDispenseTill) {
      const apiPatient = tillApiData.patient;
      const displayInstant =
        getDispenseTillDisplayInstant(selectedDispenseTill);

      reset({
        name: [apiPatient.firstName, apiPatient.surName]
          .filter(Boolean)
          .join(' '),
        phone: apiPatient.phone || '',
        email: apiPatient.email || '',
        date: displayInstant ? moment(displayInstant).format('YYYY-MM-DD') : '',
        time: displayInstant ? moment(displayInstant).format('HH:mm') : '',
        wallet: `£${tillApiData.till.wallet}`,
      });
    }
  }, [tillApiData, selectedDispenseTill, reset]);

  // Call getTill on component mount
  useEffect(() => {
    getTill(
      focusDispenseId != null ? { keepDispenseId: focusDispenseId } : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the till or focused row changes
  }, [tillId, focusDispenseId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await new ProductService().getActiveProducts();
        if (!cancelled) {
          setScanProducts(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error(
          'Failed to fetch products for till barcode scanner',
          error,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTillBarcodeScan = useCallback(
    (barcode: string) => {
      const product = findProductByBarcode(scanProducts, barcode);
      if (product) {
        navigate(PATH_DASHBOARD.till.new, {
          state: {
            [TILL_BARCODE_NAV_STATE_KEY]: activeProductToDispenseLine(product),
          },
        });
        enqueueSnackbar(`Opening Create New Sale with ${product.name}`, {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(`Product with code "${barcode}" not found`, {
          variant: 'warning',
        });
      }
    },
    [scanProducts, navigate],
  );

  useBarcodeScanner({ onScan: handleTillBarcodeScan });

  const typographyStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 100,
    color: 'black',
  };

  const buttonStyles = {
    fontWeight: '300',
    fontSize: '12px',
    px: 1,
  };

  return (
    <FormProvider methods={methods}>
      {loading && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography>Loading till data...</Typography>
        </Box>
      )}

      {/* Dispense Tills Table */}
      {tillApiData && (
        <Box sx={{ p: 2, display: 'flex', width: '100%', mb: 4 }}>
          <Box
            sx={{
              borderRadius: '15px',
              boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
              maxHeight: '400px',
              width: '100%',
              overflowY: 'auto',
              overflowX: 'auto',
            }}
          >
            <Table sx={{ minWidth: 'auto' }}>
              <TableHeadCustom headLabel={TABLE_HEAD} />
              <TableBody>
                {tillApiData.till.dispenseTills.map((dispenseTill, index) => (
                  <TableRow
                    key={index}
                    sx={(theme) => ({
                      backgroundColor:
                        selectedDispenseTill?.dispenseId ===
                        dispenseTill.dispenseId
                          ? theme.palette.action.selected
                          : 'transparent',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    })}
                  >
                    <TableCell align="center">
                      #{dispenseTill.dispenseId}
                    </TableCell>
                    <TableCell align="center">£{dispenseTill.amount}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={capitalize(dispenseTill.status)}
                        color={
                          dispenseTill.status === 'pending' ||
                          dispenseTill.status === 'partial'
                            ? 'warning'
                            : dispenseTill.status === 'paid'
                              ? 'success'
                              : dispenseTill.status === 'refunded'
                                ? 'default'
                                : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const t = getDispenseTillDisplayInstant(dispenseTill);
                        return t ? moment(t).format('DD/MM/YYYY') : 'N/A';
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      {dispenseTill.paymentMethod || 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant={
                          selectedDispenseTill?.dispenseId ===
                          dispenseTill.dispenseId
                            ? 'contained'
                            : 'outlined'
                        }
                        size="small"
                        onClick={() => handleSelectDispenseTill(dispenseTill)}
                        sx={{ fontWeight: '300', fontSize: '12px' }}
                      >
                        {selectedDispenseTill?.dispenseId ===
                        dispenseTill.dispenseId
                          ? 'Selected'
                          : 'Select'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableNoData
                  isNotFound={tillApiData.till.dispenseTills.length <= 0}
                />
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {/* Info and Wallet Section */}
      <Box
        sx={{
          px: 1,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ width: '65%' }}>
          <Typography variant="h6" sx={{ ml: '2px' }}>
            Patient Info
          </Typography>
          <RHFTextField name="name" disabled={isViewMode} />
        </Box>
        <Box sx={{ width: '35%' }}>
          <Typography variant="h6" sx={{ ml: '2px' }}>
            Wallet Balance
          </Typography>
          {/* Store credit from refunds made "to wallet"; spent via Pay From Wallet. Not editable here. */}
          <RHFTextField
            name="wallet"
            disabled
            helperText="Patient credit from wallet refunds"
          />
        </Box>
      </Box>

      {/* Main Content Section */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'start',
          gap: 1,
          mt: 4,
        }}
      >
        {/* Left Section */}
        <Box sx={{ px: 1, width: '65%' }}>
          {/* Date & Time */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ width: '50%' }}>
              <Typography variant="h6" sx={{ ml: 1 }}>
                {selectedDispenseTill?.status === 'refunded'
                  ? 'Refund date'
                  : 'Payment Date'}
              </Typography>
              <RHFTextField
                name="date"
                value={(() => {
                  const t = getDispenseTillDisplayInstant(selectedDispenseTill);
                  return t ? moment(t).format('YYYY-MM-DD') : '-';
                })()}
                disabled={isViewMode}
              />
            </Box>
            <Box sx={{ width: '50%' }}>
              <Typography variant="h6" sx={{ ml: 1 }}>
                Time
              </Typography>
              <RHFTextField
                name="time"
                value={(() => {
                  const t = getDispenseTillDisplayInstant(selectedDispenseTill);
                  return t ? moment(t).format('HH:mm') : '-';
                })()}
                disabled={isViewMode}
              />
            </Box>
          </Box>

          {/* Phone Number & Email */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 4,
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: '50%',
                border: '1px solid #ccc',
                padding: '16px 6px',
              }}
            >
              <Typography variant="h6" sx={{ ml: '2px', mb: 2 }}>
                Phone Number
              </Typography>
              <RHFTextField
                name="phone"
                fullWidth
                disabled={isViewMode}
                sx={{
                  '& .MuiInputBase-input': {
                    paddingTop: '16px',
                    paddingBottom: '16px',
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                width: '50%',
                border: '1px solid #ccc',
                padding: '16px 6px',
              }}
            >
              <Typography variant="h6" sx={{ ml: '2px', mb: 2 }}>
                E-mail
              </Typography>
              <RHFTextField
                name="email"
                fullWidth
                disabled={isViewMode}
                sx={{
                  '& .MuiInputBase-input': {
                    paddingTop: '16px',
                    paddingBottom: '16px',
                  },
                }}
              />
            </Box>
          </Box>

          {/* Payment Methods — while balance is still due */}
          {selectedDispenseTill &&
            (selectedDispenseTill.status === 'pending' ||
              selectedDispenseTill.status === 'partial') && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Typography variant="h6">Payment Methods</Typography>
                  <Button
                    variant="outlined"
                    sx={{ fontWeight: '300' }}
                    onClick={() => setOpen(true)}
                    disabled={
                      getOutstandingBalance() <= 0 || walletBalanceNum() <= 0
                    }
                  >
                    Pay From Wallet (£{tillApiData?.till?.wallet || '0'})
                  </Button>
                </Box>

                {/* Pay From Wallet Dialog */}
                <Dialog
                  open={open}
                  onClose={() => setOpen(false)}
                  sx={{ '& .MuiPaper-root': { borderRadius: '0 !important' } }}
                >
                  <DialogContent sx={{ p: 4, mt: 2 }}>
                    <Typography>
                      Pay from wallet: £{walletPayAmount().toFixed(2)} will be
                      applied to this dispense till (up to the outstanding
                      balance).
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Current wallet balance: £{walletBalanceNum().toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Order total: £
                      {Number(selectedDispenseTill?.amount ?? 0).toFixed(2)} ·
                      Outstanding: £{getOutstandingBalance().toFixed(2)}
                    </Typography>
                    {walletBalanceNum() > 0 &&
                    walletBalanceNum() < getOutstandingBalance() ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Wallet is less than the full outstanding amount; the
                        remainder can be paid using other methods.
                      </Typography>
                    ) : null}
                    {walletBalanceNum() <= 0 ? (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        No wallet balance available.
                      </Typography>
                    ) : null}
                  </DialogContent>
                  <DialogActions
                    sx={{
                      gap: 1,
                      justifyContent: 'flex-end',
                      pt: '0 !important',
                    }}
                  >
                    <Button
                      onClick={handlePayFromWallet}
                      color="success"
                      variant="outlined"
                      size="small"
                      disabled={
                        loading ||
                        getOutstandingBalance() <= 0 ||
                        walletBalanceNum() <= 0
                      }
                      sx={{ minWidth: '50px', padding: '2px 8px' }}
                    >
                      Yes
                    </Button>
                    <Button
                      onClick={() => setOpen(false)}
                      color="error"
                      variant="outlined"
                      size="small"
                      sx={{
                        minWidth: '50px',
                        padding: '2px 8px',
                        ml: '0 !important',
                      }}
                    >
                      No
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Payment Tenders */}
                <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant={cashExpanded ? 'contained' : 'outlined'}
                      fullWidth
                      onClick={() => {
                        setCashExpanded(!cashExpanded);
                        setCardExpanded(false);
                        setPaymentAmount(getOutstandingBalance().toString());
                      }}
                      sx={{ height: '56px' }}
                    >
                      Cash
                    </Button>
                    <Button
                      variant={cardExpanded ? 'contained' : 'outlined'}
                      fullWidth
                      onClick={() => {
                        setCardExpanded(!cardExpanded);
                        setCashExpanded(false);
                        setPaymentAmount(getOutstandingBalance().toString());
                      }}
                      sx={{ height: '56px' }}
                    >
                      Card (PDQ / Stripe)
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        setVoucherOpen(true);
                        setVoucherAmount(getOutstandingBalance().toString());
                      }}
                      sx={{ height: '56px' }}
                    >
                      NHS Voucher
                    </Button>
                  </Box>

                  {/* Cash Section */}
                  <Collapse in={cashExpanded}>
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        mt: 1,
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Cash Payment
                      </Typography>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        InputProps={{ startAdornment: '£' }}
                      />
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() =>
                          handleRecordPayment('cash', paymentAmount)
                        }
                        disabled={loading}
                      >
                        Record Cash Payment
                      </Button>
                    </Box>
                  </Collapse>

                  {/* Card Section */}
                  <Collapse in={cardExpanded}>
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        mt: 1,
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Card Payment
                      </Typography>

                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleStripePayment}
                        disabled={stripeLoading}
                        sx={{ mb: 2 }}
                      >
                        {stripeLoading
                          ? 'Processing...'
                          : 'Pay with Stripe (Online)'}
                      </Button>

                      <Typography
                        variant="body2"
                        sx={{ my: 2, textAlign: 'center' }}
                      >
                        - OR -
                      </Typography>

                      <Typography variant="subtitle2" gutterBottom>
                        Record External Terminal (PDQ)
                      </Typography>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        InputProps={{ startAdornment: '£' }}
                      />
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() =>
                          handleRecordPayment('card', paymentAmount)
                        }
                        disabled={loading}
                      >
                        Record PDQ Payment
                      </Button>
                    </Box>
                  </Collapse>
                </Box>

                {/* NHS Voucher Dialog */}
                <Dialog
                  open={voucherOpen}
                  onClose={() => setVoucherOpen(false)}
                >
                  <DialogContent sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      NHS Voucher
                    </Typography>
                    <TextField
                      fullWidth
                      label="Voucher Value"
                      type="number"
                      value={voucherAmount}
                      onChange={(e) => setVoucherAmount(e.target.value)}
                      InputProps={{ startAdornment: '£' }}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setVoucherOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() =>
                        handleRecordPayment('voucher', voucherAmount)
                      }
                      disabled={loading}
                    >
                      Apply Voucher
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}

          {/* Paid: actions + summary (Pay From Wallet only appears while pending/partial above) */}
          {selectedDispenseTill && selectedDispenseTill.status === 'paid' && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  mt: 2,
                }}
              >
                <Typography variant="h6">Payment</Typography>
                <Button
                  variant="outlined"
                  color="success"
                  sx={{ fontWeight: '300' }}
                  onClick={() => setRefundOpen(true)}
                  disabled={loading}
                >
                  Refund Payment
                </Button>
              </Box>
              <Box
                sx={(theme) => ({
                  p: 2,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.success.main}`,
                  mt: 2,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.background.paper
                      : '#e8f5e8',
                })}
              >
                <Typography variant="h6" color="success.main">
                  Payment Completed
                </Typography>
                <Typography variant="body2">
                  This dispense till has already been paid using{' '}
                  {selectedDispenseTill.paymentMethod || 'Unknown method'}
                </Typography>
                {selectedDispenseTill.paymentDate && (
                  <Typography variant="body2">
                    Payment Date:{' '}
                    {moment(selectedDispenseTill.paymentDate).format(
                      'DD/MM/YYYY HH:mm',
                    )}
                  </Typography>
                )}
              </Box>
            </>
          )}

          {selectedDispenseTill &&
            selectedDispenseTill.status === 'refunded' && (
              <Box
                sx={(theme) => ({
                  p: 2,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  mt: 2,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.background.neutral
                      : theme.palette.grey[100],
                })}
              >
                <Typography variant="h6" color="text.primary">
                  Refunded
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {selectedDispenseTill.refundMode === 'wallet'
                    ? 'The paid amount was credited to the patient wallet.'
                    : 'A refund has been recorded for this dispense till (reverse the payment with the customer as appropriate).'}
                </Typography>
                {selectedDispenseTill.refundAmount != null && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Refund amount: £{selectedDispenseTill.refundAmount}
                  </Typography>
                )}
                {selectedDispenseTill.refundReason ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Reason: {selectedDispenseTill.refundReason}
                  </Typography>
                ) : null}
                {selectedDispenseTill.refundedAt ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {moment(selectedDispenseTill.refundedAt).format(
                      'DD/MM/YYYY HH:mm',
                    )}
                  </Typography>
                ) : null}
              </Box>
            )}

          <Dialog
            open={refundOpen}
            onClose={() => !refundSubmitting && setRefundOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
          >
            <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
              Refund
            </DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder="Enter Refund Reason"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                disabled={refundSubmitting}
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions
              sx={{
                justifyContent: 'flex-end',
                px: 3,
                pb: 2,
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="outlined"
                color="success"
                onClick={() => handleRefund('wallet')}
                disabled={refundSubmitting}
              >
                Add on wallet
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={() => handleRefund('refund')}
                disabled={refundSubmitting}
              >
                Refund
              </Button>
            </DialogActions>
          </Dialog>

          {/* No Selection Message */}
          {!selectedDispenseTill && tillApiData && (
            <Box
              sx={{
                p: 2,
                bgcolor: '#fff3e0',
                borderRadius: 1,
                border: '1px solid #ff9800',
                mt: 2,
              }}
            >
              <Typography variant="h6" color="warning.main">
                No Dispense Till Selected
              </Typography>
              <Typography variant="body2">
                Please select a dispense till from the table above to view and
                manage payment details.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Section (Order Summary) */}
        <OrderSummary
          typographyStyles={typographyStyles}
          buttonStyles={buttonStyles}
          tillApiData={tillApiData}
          selectedDispenseTill={selectedDispenseTill} // Pass selected dispenseTill
        />
      </Box>

      {staffDisplayName ? (
        <Box
          sx={(theme) => ({
            px: 2,
            py: 2,
            mt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Staff
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {staffDisplayName}
          </Typography>
          {user?.email && user.email !== staffDisplayName ? (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {user.email}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </FormProvider>
  );
}
